import { useState } from 'react'
import GridShape from '../../components/common/GridShape'
import Input from '../../components/form/input/InputField'
import Label from '../../components/form/Label'
import { Link, useNavigate } from 'react-router'
import { EyeCloseIcon, EyeIcon } from '../../icons'
import Button from '../../components/ui/button/Button'
import PageMeta from '../../components/common/PageMeta'
import { useAuth } from '../../context/AuthContext'
import { useForm, SubmitHandler } from 'react-hook-form'
import toast from 'react-hot-toast'

interface SignInFormInputs {
    email: string
    password: string
}

export default function SignIn() {
    const [showPassword, setShowPassword] = useState(false)
    const { signIn } = useAuth()
    const navigate = useNavigate()

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm<SignInFormInputs>()

    const onSubmit: SubmitHandler<SignInFormInputs> = async data => {
        try {
            const result = await signIn(data.email, data.password)
            if (!result || !result.success) {
                toast.error(result.message)
                return
            }

            //* Login efetuado com sucesso
            navigate('/', { replace: true })
        } catch (error) {
            toast.error('Falha na autenticação. Verifique suas credenciais!')
        }
    }

    return (
        <>
            <PageMeta title='Login | MEPAGE CRM' description='Página de login do MEPAGE CRM' />
            <div className='relative flex w-full h-screen px-4 py-6 overflow-hidden bg-white z-1 dark:bg-gray-900 sm:p-0'>
                <div className='flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8'>
                    <div className='flex flex-col justify-center flex-1 w-full max-w-md mx-auto'>
                        <div>
                            <div className='mb-5 sm:mb-12'>
                                <h1 className='mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md'>
                                    Entrar
                                </h1>
                                <p className='text-sm text-gray-500 dark:text-gray-400'>
                                    Entre com o seu e-mail e senha para acessar!
                                </p>
                            </div>

                            <div>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className='space-y-6'>
                                        <div>
                                            <Label required>E-mail</Label>
                                            <Input
                                                type='email'
                                                placeholder='seunome@email.com'
                                                {...register('email', { required: 'O email é obrigatório' })}
                                            />
                                            {errors.email && (
                                                <p className='mt-1.5 text-xs text-error-500'>{errors.email.message}</p>
                                            )}
                                        </div>
                                        <div>
                                            <Label required>Senha</Label>
                                            <div className='relative'>
                                                <Input
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder='Senha'
                                                    {...register('password', {
                                                        required: 'A senha é obrigatória',
                                                        minLength: {
                                                            value: 4,
                                                            message: 'A senha deve ter no mínimo 4 caracteres'
                                                        }
                                                    })}
                                                />
                                                {errors.password && (
                                                    <p className='mt-1.5 text-xs text-error-500'>
                                                        {errors.password.message}
                                                    </p>
                                                )}

                                                <span
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className='absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2'
                                                >
                                                    {showPassword ? (
                                                        <EyeIcon className='fill-gray-500 dark:fill-gray-400' />
                                                    ) : (
                                                        <EyeCloseIcon className='fill-gray-500 dark:fill-gray-400' />
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <Button className='w-full' size='sm'>
                                                Entrar
                                            </Button>
                                        </div>
                                        <div className='flex items-center justify-center'>
                                            <Link
                                                to='/'
                                                className='text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400'
                                            >
                                                Esqueceu a senha?
                                            </Link>
                                        </div>
                                    </div>
                                </form>

                                <div className=' flex justify-center mt-5'>
                                    <p className='text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start'>
                                        Não possui uma conta? {''}
                                        <Link
                                            to='/'
                                            className='text-brand-500 hover:text-brand-600 dark:text-brand-400'
                                        >
                                            Cadastre-se
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='relative items-center justify-center flex-1 hidden p-8 z-1 bg-brand-950 dark:bg-white/5 lg:flex'>
                    {/* <!-- ===== Common Grid Shape Start ===== --> */}
                    <GridShape />
                    <div className='flex flex-col items-center max-w-xs'>
                        <Link to='/' className='block mb-4'>
                            <img src='./images/logo/logo-dark.png' alt='Logo' className='w-64' />
                        </Link>
                        <p className='text-center text-gray-400 dark:text-white/60'>Seu CRM para o seu negócio</p>
                    </div>
                </div>
            </div>
        </>
    )
}
